import React, { Fragment } from 'react';
import PropTypes, { InferProps } from 'prop-types';
import styled from '@emotion/styled';
import { List, ListItem } from '.';
import Headline3 from '../Typography/Heading/Headline3';
import Headline6 from '../Typography/Heading/Headline6';
import breakpoints from '../Theme/breakpoints';
import Skeleton from '../SkeletonLoader/Skeleton';
import grey from '../Palettes/grey';
import overlay from '../Palettes/overlay';

const ListItemVisualsContainer = styled.div`
  text-align: center;
  margin-right: 24px;
`;

const listItemDatePropertyTypes = {
  day: PropTypes.string.isRequired,
  month: PropTypes.string.isRequired,
  showLoader: PropTypes.bool,
};

type ListItemDateProperties = InferProps<typeof listItemDatePropertyTypes>;

const ListItemDate = ({ day, month, showLoader }: ListItemDateProperties) => (
  <ListItemVisualsContainer>
    {showLoader ? (
      <Skeleton variant="rectangular" width="35px">
        <Headline3 tag="div">&nbsp;</Headline3>
      </Skeleton>
    ) : <Headline3 tag="div" color="textSecondary">{day}</Headline3>}

    {showLoader ? (
      <Skeleton variant="rectangular" width="35px">
        <Headline6 tag="div">&nbsp;</Headline6>
      </Skeleton>
    ) : <Headline6 tag="div" color="textSecondary">{month}</Headline6>}

  </ListItemVisualsContainer>
);

ListItemDate.propTypes = listItemDatePropertyTypes;

const StyledListItem = styled(ListItem)`
  &&& {
    justify-content: space-between;
    padding: 24px 16px;

    @media (min-width: ${breakpoints.md}px) {
      padding: 24px 0 0;
    }
  }
`;

const ListItemImageContainer = styled.div`
  &&& {
    margin-left: 16px;
  }
`;

const SpaceBetweenListItem = styled(ListItem)`
  &&& {
    justify-content: space-between;
    padding: 24px 16px;
    border-bottom: 1px solid ${grey[325]};
  }

  div:last-of-type {
    text-align: right;
  }
`;

const TableListHeading = styled.div`
  && {
    margin: 0;
    text-transform: uppercase;
    font-size: ${({ theme }) => theme.sizes.body1};
    font-weight: normal; /* fixes font-weight issue Firefox */
    color: ${overlay[900]};
    font-family: ${({ theme }) => theme.fonts.secondary};
    display: block;
  }
`;

const tableListPropertyTypes = {
  className: PropTypes.string,
  listItemClassName: PropTypes.string,
  headings: PropTypes.arrayOf(PropTypes.string),
  rows: PropTypes.arrayOf(
    PropTypes.arrayOf(PropTypes.node).isRequired,
  ),
};

type TableListProperties = InferProps<
  typeof tableListPropertyTypes
> & {
  className?: string;
  listItemClassName?: string;
  headings?: string[];
  rows?: React.ReactNode[][];
};

const TableList = ({
  className,
  listItemClassName,
  headings,
  rows,
}: TableListProperties) => {
  const classNameValue = className ?? 'fu-mdc-list';
  const listItemClassNameValue = listItemClassName ?? 'fu-list-item-content';

  return (
    <List className={classNameValue}>
      <SpaceBetweenListItem className={listItemClassNameValue}>
        {(headings ?? []).map((heading) => (
          <TableListHeading key={heading}>
            {heading}
          </TableListHeading>
        ))}
      </SpaceBetweenListItem>
      {(rows ?? []).map((row, rowIndex) => {
        const rowKey = `row-${rowIndex}`;
        return (
          <Fragment key={rowKey}>
            <SpaceBetweenListItem
              className={listItemClassNameValue}
            >
              {row.map((cell, cellIndex) => {
                const cellKey = `row-${rowIndex}-cell-${cellIndex}`;
                return (
                  <div key={cellKey}>
                    {cell}
                  </div>
                );
              })}
            </SpaceBetweenListItem>
          </Fragment>
        );
      })}
    </List>
  );
};

TableList.propTypes = tableListPropertyTypes;

export {
  ListItemDate,
  StyledListItem,
  ListItemImageContainer,
  TableListHeading,
  TableList,
};
